import { api } from "@/api/client";
import type { Decision, FromCategory, PageRange, SplitInfo, Stats } from "@/types";

// The approval_* queues are vendor bill approval tabs (ApprovalQueueTable), not decisions.
export type Queue = "needs_review" | "forwarding" | "spam_flagged" | "forwarded" | "all"
  | "approval_pending" | "approval_rejected" | "approval_approved";
// None of these sends mail: invoices are billed in NetSuite, never forwarded on.
export type Override = "confirm_spam" | "dismiss" | "to_review" | "to_forwarding";
export type SortKey = "date" | "received" | "from" | "subject" | "read_as" | "confidence" | "action" | "due";
export type SortDir = "asc" | "desc";

export interface DecisionFilters {
  queue: Queue;
  q?: string;
  from?: string;
  from_category?: FromCategory | "";
  vendor_id?: string;
  read_as?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
  // A hold reason key, or "none" for items not held in this queue.
  hold?: string;
  // "1": only items unread for the viewer.
  unread?: string;
  sort: SortKey;
  dir: SortDir;
  page: number;
  per_page: number;
}

export interface DecisionList {
  items: Decision[];
  total: number;
  page: number;
  per_page: number;
}

export async function fetchDecisions(filters: DecisionFilters): Promise<DecisionList> {
  const { data } = await api.get<DecisionList>("/decisions", { params: filters });
  return data;
}

export async function fetchDecision(id: number): Promise<Decision> {
  const { data } = await api.get<Decision>(`/decisions/${id}`);
  return data;
}

export async function fetchStats(): Promise<Stats> {
  const { data } = await api.get<Stats>("/decisions/stats");
  return data;
}

export async function applyOverride(id: number, action: Override): Promise<Decision> {
  const { data } = await api.post<Decision>(`/decisions/${id}/${action}`);
  return data;
}

// Set why an item is parked, or clear it with an empty reason.
export async function setHold(id: number, reason: string, note: string): Promise<Decision> {
  const { data } = await api.post<Decision>(`/decisions/${id}/hold`, { reason, note });
  return data;
}

export interface BulkOverrideResult {
  override: Override;
  succeeded: number[];
  failed: Array<{ id: number; error: string }>;
}

// Apply one override to many decisions at once (e.g. dismiss selected non-bills).
// The override is sent under `override`, not `action` — Rails' router reserves
// `action` for the controller action name.
export async function bulkOverride(ids: number[], action: Override): Promise<BulkOverrideResult> {
  const { data } = await api.post<BulkOverrideResult>("/decisions/bulk", { ids, override: action });
  return data;
}

// Read state is per user and covers the item's whole email thread.
export async function setReadState(ids: number[], read: boolean): Promise<void> {
  await api.post(read ? "/decisions/mark_read" : "/decisions/mark_unread", { ids });
}

export async function fetchSplitInfo(id: number): Promise<SplitInfo> {
  const { data } = await api.get<SplitInfo>(`/decisions/${id}/split`);
  return data;
}

// URL for the browser's PDF viewer to preview one invoice range inline.
export function splitPartUrl(id: number, range: PageRange): string {
  return `/api/v1/decisions/${id}/split_part?start=${range.start}&end=${range.end}`;
}

